import locales from '@spa/shared/constants/locales';
import { allowedLanguages } from '@spa/shared/constants/dateLocales';
import { format as dateFnsFormat, parse, isDate, add, differenceInMonths, isBefore, isAfter, isEqual } from 'date-fns';
import { DATE_PICKER_SUPPORTED_LOCALES } from '@spa/shared/constants/date-picker-supported-locales';

export const decade = year => (year > 0 ? 1 : -1) * Math.floor(year / 10) * 10;

export const getFormatFunctions = locale => ({
  formatDay: date => dateFnsFormat(date, 'dd', { locale }),
  formatWeekDay: date => dateFnsFormat(date, 'eeeeee', { locale }),
  formatMonth: date => dateFnsFormat(date, 'MMMM', { locale }),
  format: (date, format) => (date ? dateFnsFormat(date, format, { locale }) : ''),
  stringToDate: (date, format = 'yyyy-MM-dd') => (date ? parse(date, format, new Date()) : null),
  dateToString: (date, format = 'yyyy-MM-dd') => (date ? dateFnsFormat(date, format, { locale }) : null)
});

export const getIntlLocale = locale => (locale === 'zh' ? 'zh-CN' : locale);

export const getIntl = async locale => {
  if (locale === allowedLanguages.EN) {
    return getFormatFunctions();
  }

  const localeTranslations = await import(`date-fns/locale/${getIntlLocale(locale)}/index.js`);

  return getFormatFunctions(localeTranslations);
};

export const getDateLocale = language => {
  const invertedLocales = Object.assign({}, ...Object.entries(locales).map(([a, b]) => ({ [b]: a })));
  const index = Object.keys(invertedLocales).find(lng => lng === language || lng.substring(0, 2) === language);

  return allowedLanguages[invertedLocales[index || allowedLanguages.EN]];
};

export const formatDate = (date, language = 'en', pattern = 'yyyy-MM-dd') => {
  const dateToFormat = isDate(date) ? date : new Date();

  return dateFnsFormat(dateToFormat, pattern, { locale: DATE_PICKER_SUPPORTED_LOCALES[language] });
};

export const daysToMonths = days => {
  const startDate = new Date();

  return differenceInMonths(add(startDate, { days }), startDate);
};

export const numberToMonth = month =>
  /^-?\d+$/.test(month) && month > 0 && month <= 12 ? `0${month}`.slice(-2) : '01';

export const monthLabelToNumber = (monthLabel, language = 'en') =>
  parse(monthLabel, 'MMMM', new Date(), { locale: DATE_PICKER_SUPPORTED_LOCALES[language] }).getMonth();

export const isBeforeOrSame = (date1, date2) => isBefore(date1, date2) || isEqual(date1, date2);

export const isAfterOrSame = (date1, date2) => isAfter(date1, date2) || isEqual(date1, date2);
